import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';

import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import CustomInput from 'components/CustomInput/CustomInput.js';
import ImageUpload from 'components/CustomUpload/ImageUpload.js';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentUser,
} from '../../features/user/userSlice';
import { isValidEmail } from '../../utils';
import { useUpdateUserMutation } from 'api/user.api';
import uploadImageFile from 'api/imageAPI';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    alignContent: 'center',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

export default function ProfileEditor(props) {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);

  const [fullName, setFullName] = useState('');
  const [fullNameState, setFullNameState] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameState, setDisplayNameState] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailAddressState, setEmailAddressState] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [updateUser] = useUpdateUserMutation();

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (user !== null && props.open) {
      setFullName(user.full_name);
      setDisplayName(user.display_name);
      setEmailAddress(user.email_address);
      setPhoneNumber(user.phone_number);
      setAvatar(user.avatar);
      setInitialized(true);
    }
  }, [user, initialized, props.open]);

  const classes = useStyles();

  const verifyLength = (value, length, required = true) => {
    if (value && value.length >= length) {
      return true;
    } else if (!required && (!value || value.length === 0)) return true;
    return false;
  };

  // eslint-disable-next-line
  const handleSubmit = async (e) => {
    e.preventDefault();

    var error = false;

    if (!verifyLength(fullName, 3)) {
      error = true;
      setFullNameState('error');
    }
    if (!verifyLength(displayName, 3)) {
      error = true;
      setDisplayNameState('error');
    }
    if (!isValidEmail(emailAddress)) {
      error = true;
      setEmailAddressState('error');
    }

    if (!error) {
      const userData = {
        ...user,
        full_name: fullName,
        display_name: displayName,
        avatar: avatar,
        email_address: emailAddress,
        phone_number: phoneNumber,
      };
      
      if (userData.avatar){
        userData.avatar = await uploadImageFile(userData.client_id, userData.avatar)
      }
      await updateUser(userData).unwrap();
      setInitialized(false);

      props.handleClose();
    }
  };

  return (
    <div>
      <Dialog open={props.open} aria-labelledby='form-dialog-title'>
        <DialogTitle id='form-dialog-title'>Edit your profile</DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={7}>
              <CustomInput
                success={fullNameState === 'success'}
                error={fullNameState === 'error'}
                labelText='Full Name'
                id='fullname'
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  defaultValue: fullName,
                  onChange: (event) => {
                    if (verifyLength(event.target.value, 3)) {
                      setFullNameState('success');
                    } else {
                      setFullNameState('error');
                    }
                    setFullName(event.target.value);
                  },
                  autoComplete: 'off',
                }}
              />
              <CustomInput
                success={displayNameState === 'success'}
                error={displayNameState === 'error'}
                labelText='Display Name'
                id='displayname'
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  defaultValue: displayName,
                  onChange: (event) => {
                    if (verifyLength(event.target.value, 3)) {
                      setDisplayNameState('success');
                    } else {
                      setDisplayNameState('error');
                    }
                    setDisplayName(event.target.value);
                  },
                  autoComplete: 'off',
                }}
              />
              <CustomInput
                success={emailAddressState === 'success'}
                error={emailAddressState === 'error'}
                labelText='Email Address'
                id='email'
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  defaultValue: emailAddress,
                  onChange: (event) => {
                    if (isValidEmail(event.target.value)) {
                      setEmailAddressState('success');
                    } else {
                      setEmailAddressState('error');
                    }
                    setEmailAddress(event.target.value);
                  },
                  type: 'email',
                  autoComplete: 'off',
                }}
              />
              <CustomInput
                labelText='Telephone Number'
                id='telephone'
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  defaultValue: phoneNumber,
                  onChange: (event) => {
                    setPhoneNumber(event.target.value);
                  },
                  autoComplete: 'off',
                }}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={4}>
              <div className='fileinput text-center'>
                <ImageUpload
                  image={avatar}
                  avatar={true}
                  getImageFile={(image) => setAvatar(image)}
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={props.handleClose} color='secondary'>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn onClick={handleSubmit} color='primary'>
            Save Changes
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
